<div class="dr-wrap">

  <div class="dr-toolbar">
    <h2 class="dr-title">Routes</h2>
    <p class="dr-note">
      A route is the ordered list of kantiner a driver works through in a shift.
    </p>
  </div>

  <div class="dr-error" *ngIf="error">{{ error }}</div>
  <div class="dr-empty" *ngIf="loading">Loading routes…</div>

  <div class="dr-layout" *ngIf="!loading">

    <!-- route list ---------------------------------------------------------->
    <div class="dr-list-pane">
      <div class="dr-list-head">
        <div class="dr-input">
          <mat-icon>search</mat-icon>
          <input [(ngModel)]="routeSearch" (ngModelChange)="applyRouteFilter()"
                 placeholder="Search routes…">
          <button type="button" *ngIf="routeSearch" class="dr-input-clear"
                  (click)="routeSearch = ''; applyRouteFilter()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <button mat-mini-fab color="primary" class="dr-add-route" *ngIf="canEdit"
                (click)="openNewRoute()" matTooltip="New route">
          <mat-icon>add</mat-icon>
        </button>
      </div>

      <div class="dr-route-item" *ngFor="let r of filteredRoutes; trackBy: trackByRoute"
           [class.dr-route-selected]="r.id === selectedId"
           [class.dr-route-off]="r.active === false"
           (click)="select(r)">
        <div class="dr-route-main">
          <span class="dr-route-name">{{ r.name }}</span>
          <span class="dr-spacer"></span>
          <!-- Everything you can do to a route without opening it. -->
          <button mat-icon-button class="dr-row-action" *ngIf="canEdit"
                  [matMenuTriggerFor]="rowMenu" (click)="$event.stopPropagation()"
                  matTooltip="More">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #rowMenu="matMenu">
            <button mat-menu-item [matMenuTriggerFor]="driverMenu">
              <mat-icon>person</mat-icon><span>Assign driver</span>
            </button>
            <button mat-menu-item (click)="duplicateRoute(r, $event)" [disabled]="!!duplicatingId">
              <mat-icon>content_copy</mat-icon><span>Duplicate</span>
            </button>
            <button mat-menu-item (click)="deleteRoute(r)">
              <mat-icon color="warn">delete</mat-icon><span>Delete route</span>
            </button>
          </mat-menu>
          <mat-menu #driverMenu="matMenu">
            <button mat-menu-item (click)="assignDriver(r, null, $event)">— none —</button>
            <button mat-menu-item *ngFor="let d of activeDrivers; trackBy: trackByDriver"
                    (click)="assignDriver(r, d, $event)">
              {{ d.name || d.email }}
            </button>
          </mat-menu>
        </div>
        <div class="dr-route-meta">
          <span class="dr-meta-item" [class.dr-meta-warn]="!r.stops?.length">
            <mat-icon>storefront</mat-icon>
            {{ r.stops?.length || 0 }} {{ r.stops?.length === 1 ? 'kantine' : 'kantiner' }}
          </span>
          <span class="dr-meta-item" [class.dr-meta-warn]="!r.defaultDriverUid">
            <mat-icon>person</mat-icon>
            {{ r.defaultDriverUid ? driverLabel(r.defaultDriverUid) : 'No driver' }}
          </span>
          <span class="dr-chip dr-chip-off" *ngIf="r.active === false">Inactive</span>
        </div>
      </div>

      <div class="dr-empty" *ngIf="!filteredRoutes.length">
        {{ routes.length ? 'No route matches that search.' : 'No routes yet. Press + to create one.' }}
      </div>
    </div>

    <!-- editor -------------------------------------------------------------->
    <div class="dr-edit-pane" *ngIf="selected">

      <div class="dr-edit-head">
        <div class="dr-edit-title">
          <h3>{{ selected.name }}</h3>
          <div class="dr-edit-sub">
            <mat-icon>person</mat-icon>
            {{ selected.defaultDriverUid ? driverLabel(selected.defaultDriverUid) : 'No default driver' }}
            <span class="dr-chip dr-chip-off" *ngIf="selected.active === false">Inactive</span>
          </div>
          <div class="dr-edit-sub">
            <mat-icon>flag</mat-icon>
            {{ selected.depot ? 'Loads at ' + selected.depot.name : 'No loading point — set it with Edit' }}
          </div>
        </div>

        <span class="dr-spacer"></span>

        <button mat-stroked-button (click)="openEdit()" *ngIf="canEdit">
          <mat-icon>edit</mat-icon> Edit
        </button>
        <button mat-stroked-button (click)="closeEditor()">Close</button>
        <button mat-flat-button color="primary" (click)="save()"
                [disabled]="!dirty || saving || !canEdit">
          {{ saving ? 'Saving…' : 'Save route' }}
        </button>
        <button mat-button color="warn" (click)="deleteRoute(selected)" *ngIf="canEdit">
          <mat-icon>delete</mat-icon> Delete route
        </button>
      </div>

      <!-- all stops on one map, with the driving route between them -->
      <div class="dr-map-card">
        <div class="dr-section-head">
          <strong>Map</strong>
          <span class="dr-muted" *ngIf="mapKm">
            About {{ mapKm.toFixed(0) }} km · {{ mapMinutes.toFixed(0) }} min driving between the stops
          </span>
          <span class="dr-spacer"></span>
          <button mat-button (click)="toggleMapOpen()">{{ mapOpen ? 'Hide map' : 'Show map' }}</button>
        </div>
        <div [hidden]="!mapOpen">
          <div class="dr-map-wrap">
            <div #mapEl class="dr-map" [class.dr-map-big]="mapExpanded"></div>
            <button type="button" class="dr-map-expand" (click)="toggleMapExpanded()">
              <mat-icon>{{ mapExpanded ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
              {{ mapExpanded ? 'Shrink map' : 'Extend map' }}
            </button>
            <button type="button" class="dr-map-base" (click)="toggleMapBase()">
              {{ mapBase === 'sat' ? 'Street map' : 'Satellite' }}
            </button>
          </div>
          <p class="dr-map-hint" *ngIf="mapKm">
            Each leg has its own colour, from blue (first) to pink (last), with arrows showing the
            direction. Hover a leg to see where it goes and how long it is.
          </p>
          <p class="dr-map-warn" *ngIf="mapNote">{{ mapNote }}</p>
        </div>
      </div>

      <div class="dr-columns">

        <!-- stops in driving order -->
        <div class="dr-stops">
          <div class="dr-section-head">
            <strong>Driving order</strong>
            <span class="dr-chip">{{ selected.stops?.length || 0 }}</span>
            <span class="dr-muted">Drag to reorder</span>
            <button mat-stroked-button class="dr-optimize" *ngIf="canEdit"
                    (click)="optimizeOrder()" [disabled]="optimizing"
                    matTooltip="Propose a shorter driving order from the stops' locations">
              <mat-icon>route</mat-icon>
              {{ optimizing ? (optimStep || 'Working…') : 'Optimize order' }}
            </button>
          </div>

          <div class="dr-optim-msg" *ngIf="optimMessage">{{ optimMessage }}</div>

          <div class="dr-optim-proposal" *ngIf="optimProposal">
            <div class="dr-optim-head">
              Shorter order found:
              <strong>{{ optimProposal.beforeKm.toFixed(1) }} km →
                {{ optimProposal.afterKm.toFixed(1) }} km</strong>
              ({{ ((1 - optimProposal.afterKm / optimProposal.beforeKm) * 100).toFixed(0) }}% less between stops)
            </div>
            <ol class="dr-optim-list">
              <li *ngFor="let s of optimProposal.stops">{{ s.systemName || s.systemId }}</li>
            </ol>
            <div class="dr-optim-actions">
              <button mat-flat-button color="primary" (click)="applyOptimization()">Apply this order</button>
              <button mat-button (click)="dismissOptimization()">Keep current</button>
            </div>
          </div>

          <!-- Which days each stop is served. Mon–Fri is the default and is
               stored as "no setting"; anything else is materialised only on
               those days. Tick the stops, tick the days, apply. -->
          <div class="dr-bulk" *ngIf="selected.stops?.length && canEdit">
            <div class="dr-bulk-row">
              <button mat-button (click)="selectAllStops()">Select all</button>
              <button mat-button (click)="clearStopSelection()" [disabled]="!selectedCount">Clear</button>
              <span class="dr-bulk-count" [class.dr-bulk-on]="selectedCount">
                {{ selectedCount }} selected
              </span>
            </div>
            <div class="dr-bulk-row" *ngIf="selectedCount">
              <span class="dr-muted">Days:</span>
              <label class="dr-day" *ngFor="let d of dayNames; let i = index"
                     [class.dr-day-on]="bulkDays[i]">
                <input type="checkbox" [(ngModel)]="bulkDays[i]"> {{ d }}
              </label>
              <button mat-stroked-button (click)="applyWeekdays()">Apply days</button>
              <button mat-button (click)="loadDaysFromSelection()"
                      matTooltip="Load the days of the first selected stop">Load</button>
              <button mat-button (click)="resetWeekdays()">Back to Mon–Fri</button>
              <button mat-button color="warn" (click)="removeSelectedStops()">Remove</button>
            </div>
            <div class="dr-muted dr-bulk-note" *ngIf="selectedCount">
              Days decide which stops the driver's own daily run contains. "Generate run" below
              deliberately takes the whole route.
            </div>
          </div>

          <div cdkDropList (cdkDropListDropped)="dropStop($event)" class="dr-stop-list">
            <div class="dr-stop" *ngFor="let s of selected.stops; let i = index; trackBy: trackByStop"
                 cdkDrag [class.dr-stop-picked]="isStopSelected(s)">
              <mat-icon class="dr-handle" cdkDragHandle>drag_indicator</mat-icon>
              <mat-checkbox class="dr-stop-check" *ngIf="canEdit"
                            [checked]="isStopSelected(s)" (change)="toggleStopSelection(s)">
              </mat-checkbox>
              <span class="dr-stop-order">{{ i + 1 }}</span>
              <div class="dr-stop-body">
                <div class="dr-stop-name">
                  {{ s.systemName || s.systemId }}
                  <span class="dr-days" [class.dr-days-custom]="isCustomWeekdays(s)"
                        (click)="toggleStopSelection(s)"
                        matTooltip="Which days this kantine is served. Click to select the stop.">
                    {{ weekdaysLabel(s) }}
                  </span>
                </div>
                <input class="dr-stop-note" [(ngModel)]="s.note" (ngModelChange)="markDirty()"
                       placeholder="Note for the driver — access, key, contact…">
              </div>
              <label class="dr-minutes" matTooltip="Expected minutes at this stop">
                <input type="number" min="0" [(ngModel)]="s.expectedMinutes"
                       (ngModelChange)="markDirty()">
                <span>min</span>
              </label>
              <button mat-icon-button (click)="openGuide(s)"
                      matTooltip="How to find it — photos, floor, entrance pin">
                <mat-icon>place</mat-icon>
              </button>
              <button mat-icon-button (click)="removeStop(i)" matTooltip="Remove from route">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <div class="dr-empty" *ngIf="!selected.stops?.length">
            No kantiner yet. Add them from the list on the right.
          </div>
        </div>

        <!-- kantine picker -->
        <div class="dr-picker">
          <div class="dr-section-head">
            <strong>Kantiner</strong>
            <span class="dr-muted">Not yet on this route</span>
          </div>

          <div class="dr-input dr-search-full">
            <mat-icon>search</mat-icon>
            <input [(ngModel)]="systemSearch" (ngModelChange)="applySystemFilter()"
                   placeholder="Search by name or number…">
            <button type="button" *ngIf="systemSearch" class="dr-input-clear"
                    (click)="systemSearch = ''; applySystemFilter()">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="dr-picker-list">
            <div class="dr-picker-item" *ngFor="let s of availableSystems; trackBy: trackBySystem"
                 (click)="addStop(s)">
              <mat-icon class="dr-add">add_circle_outline</mat-icon>
              <span class="dr-picker-name">{{ s.name }}</span>
              <span class="dr-muted" *ngIf="s.number">#{{ s.number }}</span>
              <span class="dr-spacer"></span>
              <!-- Switched-off kantiner are shown, not hidden: a route may well
                   need to visit one, and an empty picker reads as a bug. -->
              <span class="dr-chip dr-chip-off" *ngIf="!s.isActive">Off</span>
            </div>
            <div class="dr-empty" *ngIf="!availableSystems.length">
              {{ systemSearch ? 'No kantine matches that search.' : 'Every kantine is already on this route.' }}
            </div>
          </div>
        </div>
      </div>

      <!-- run generation ---------------------------------------------------->
      <details class="dr-run">
        <summary class="dr-run-summary">
          <strong>Generate a run for a specific day</strong>
          <span class="dr-muted">Optional — a driver's route appears in the app by itself</span>
        </summary>
        <p class="dr-muted dr-run-help">
          Use this to plan a day in advance so the office can see it, to hand the round to
          someone else, or to include stops that weekday would skip (this takes the
          <em>whole</em> route). It also sends the driver a "route ready" notification.
          Safe to repeat until the driver starts.
        </p>
        <div class="dr-run-row">
          <mat-form-field appearance="outline" class="dr-field-sm">
            <mat-label>Date</mat-label>
            <input matInput type="date" [(ngModel)]="runDate">
          </mat-form-field>

          <mat-form-field appearance="outline" class="dr-field">
            <mat-label>Driver</mat-label>
            <mat-select [(ngModel)]="runDriverUid">
              <mat-option value="">— unassigned —</mat-option>
              <mat-option *ngFor="let d of activeDrivers" [value]="d.uid">
                {{ d.name || d.email }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <button mat-flat-button color="primary" (click)="generateRun()"
                  [disabled]="generatingRun || !canEdit">
            <mat-icon>event_available</mat-icon>
            {{ generatingRun ? 'Generating…' : 'Generate run' }}
          </button>
        </div>
      </details>
    </div>

    <div class="dr-edit-pane dr-edit-empty" *ngIf="!selected">
      Pick a route on the left, or press + to create one, to assign kantiner and a driver.
    </div>
  </div>
</div>
